import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr, fmtDate } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import {
  FloppyDiskIcon, ArrowClockwiseIcon, BuildingOfficeIcon, InfoIcon, NotePencilIcon, MapPinIcon, CaretRightIcon,
  StorefrontIcon, WarningIcon, CheckCircleIcon, ClockCountdownIcon, ShieldCheckIcon,
  CheckIcon
} from "@phosphor-icons/react";
import DocumentConfigSection from "@/pages/_settings/DocumentConfig";
import FiscalAuthorizations, { TIPO_LABEL } from "@/pages/_settings/FiscalAuthorizations";
import FiscalReview from "@/pages/_settings/FiscalReview";

/**
 * Configuración de documentos y datos fiscales — organizada por empresa y sucursal:
 *   Configuración → AGROPORT (datos comunes: razón social, nombre comercial, RTN…)
 *                 → Sucursal (dirección, contacto, establecimiento / punto de emisión, configuración por
 *                   documento, notas, autorizaciones fiscales e historial).
 * Las sucursales salen del MISMO registro de "Empresas y sucursales": una sucursal nueva aparece aquí sola.
 * Los documentos emitidos conservan los datos, la dirección, la autorización y la nota con que se generaron.
 */
const inp = "w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green outline-none";

export default function CompanySettingsPage() {
  const [sp, setSp] = useSearchParams();
  const empresaId = sp.get("empresa") || "";
  const sucursalId = sp.get("sucursal") || "";
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/org");
      setOrg(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const empresa = org?.empresas.find((e) => e.id === empresaId);
  const sucursal = empresa?.sucursales.find((b) => b.id === sucursalId);
  const go = (e, s) => setSp(e ? (s ? { empresa: e, sucursal: s } : { empresa: e }) : {});

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="company-settings-page">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-agro-green mb-1">PANEL DE ADMINISTRACIÓN GENERAL</div>
            <h1 className="font-heading text-3xl">Configuración de documentos y datos fiscales</h1>
            <nav className="flex flex-wrap items-center gap-1 text-sm mt-2" data-testid="org-breadcrumb">
              <button onClick={() => go()} className={empresa ? "text-text-secondary hover:text-agro-green" : "text-text-primary"}>Empresas</button>
              {empresa && <><CaretRightIcon size={12} className="text-text-muted" />
                <button onClick={() => go(empresa.id)} className={sucursal ? "text-text-secondary hover:text-agro-green" : "text-text-primary font-semibold"}>{empresa.codigo}</button></>}
              {sucursal && <><CaretRightIcon size={12} className="text-text-muted" />
                <span className="text-text-primary font-semibold">{sucursal.nombre}</span></>}
            </nav>
          </div>
          <button aria-label="Recargar" onClick={load} disabled={loading} className="p-2 border border-border rounded-md text-text-secondary hover:text-agro-green disabled:opacity-50" data-testid="reload-settings">
            <ArrowClockwiseIcon size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        {!org ? (
          <div className="text-center text-text-muted py-12">Cargando…</div>
        ) : sucursal ? (
          <BranchView key={sucursal.id} empresa={empresa} sucursalId={sucursal.id} isAdmin={isAdmin} onChanged={load} />
        ) : empresa ? (
          <CompanyView empresa={empresa} isAdmin={isAdmin} pendientes={org.pendientes} onOpen={(s) => go(empresa.id, s)}
                       onChanged={load} />
        ) : (
          <div className="space-y-5">
            <FiscalReview isAdmin={isAdmin} onResolved={load} />
            <div className="grid sm:grid-cols-2 gap-4">
              {org.empresas.map((e) => (
                <button key={e.id} type="button" onClick={() => go(e.id)} data-testid={`org-empresa-${e.codigo}`}
                  className="text-left bg-card border border-border rounded-lg p-5 hover:border-agro-green/60 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-black" style={{ backgroundColor: e.color || "#02BF44" }}>
                      {(e.codigo || "").slice(0, 2)}
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading text-xl">{e.codigo}</div>
                      <div className="text-xs text-text-secondary truncate">{e.identidad?.nombre_legal} · RTN {e.identidad?.rtn || "—"}</div>
                    </div>
                    <CaretRightIcon size={18} className="ml-auto text-agro-green" />
                  </div>
                  <div className="mt-3 text-xs text-text-muted">
                    {e.sucursales.length} sucursal{e.sucursales.length === 1 ? "" : "es"}: {e.sucursales.map((b) => b.nombre).join(" · ")}
                  </div>
                </button>
              ))}
            </div>
            <PendingPanel pendientes={org.pendientes} empresas={org.empresas} isAdmin={isAdmin} onChanged={load} />
          </div>
        )}
      </div>
    </Layout>
  );
}

/* ================================================================== empresa */
function CompanyView({ empresa, isAdmin, pendientes, onOpen, onChanged }) {
  const [f, setF] = useState({ ...(empresa.identidad || {}) });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF({ ...(empresa.identidad || {}) }); }, [empresa]);
  const set = (k, v) => setF((d) => ({ ...d, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { nombre_legal, marca, tagline, rtn, redes, sitio_web, zona_horaria } = f;
      await api.put("/settings/company", { nombre_legal, marca, tagline, rtn, redes, sitio_web, zona_horaria });
      toast.success("Datos comunes de la empresa guardados. Aplican a todas las sucursales.");
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-5">
      <Section title={`Datos comunes de ${empresa.codigo}`} icon={BuildingOfficeIcon}
               desc="Compartidos por todas las sucursales (encabezado de los documentos y correos). No se repiten en cada sucursal."
               action={isAdmin && (
                 <button onClick={save} disabled={saving} data-testid="save-settings"
                   className="bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
                   <FloppyDiskIcon size={16} weight="bold" /> {saving ? "Guardando…" : "Guardar"}
                 </button>)}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Razón social" hint="Nombre legal registrado">
            <input value={f.nombre_legal || ""} onChange={(e) => set("nombre_legal", e.target.value)} disabled={!isAdmin} className={inp} data-testid="input-nombre-legal" />
          </Field>
          <Field label="Nombre comercial">
            <input value={f.marca || ""} onChange={(e) => set("marca", e.target.value)} disabled={!isAdmin} className={inp} data-testid="input-marca" />
          </Field>
          <Field label="RTN" hint="Registro Tributario Nacional (14 dígitos)">
            <input value={f.rtn || ""} onChange={(e) => set("rtn", e.target.value)} maxLength={14} disabled={!isAdmin} className={`${inp} font-mono`} data-testid="input-rtn" />
          </Field>
          <Field label="Lema">
            <input value={f.tagline || ""} onChange={(e) => set("tagline", e.target.value)} disabled={!isAdmin} className={inp} data-testid="input-tagline" />
          </Field>
          <Field label="Redes sociales"><input value={f.redes || ""} onChange={(e) => set("redes", e.target.value)} disabled={!isAdmin} className={inp} data-testid="input-redes" /></Field>
          <Field label="Sitio web"><input value={f.sitio_web || ""} onChange={(e) => set("sitio_web", e.target.value)} disabled={!isAdmin} className={inp} data-testid="input-web" /></Field>
          <Field label="Zona horaria" hint="Las autorizaciones fiscales programadas entran en vigor a las 00:00 de esta zona.">
            <select value={f.zona_horaria || "America/Tegucigalpa"} onChange={(e) => set("zona_horaria", e.target.value)} disabled={!isAdmin}
                    className={inp} data-testid="input-zona">
              {["America/Tegucigalpa", "America/Guatemala", "America/El_Salvador", "America/Managua", "America/Costa_Rica",
                "America/Panama", "America/Mexico_City"].map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </Field>
        </div>
        <div className="text-[11px] text-text-muted">El logotipo institucional es el mismo para todas las sucursales.</div>
      </Section>

      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="font-heading text-lg">Sucursales de {empresa.codigo}</h2>
            <div className="text-[11px] text-text-muted">Cada sucursal tiene su propia dirección, contacto, autorizaciones fiscales, numeración y notas.</div>
          </div>
          <Link to="/empresas" className="text-xs text-agro-green hover:underline">Agregar sucursal en Empresas y sucursales</Link>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {empresa.sucursales.map((b) => (
            <button key={b.id} type="button" onClick={() => onOpen(b.id)} data-testid={`org-sucursal-${b.codigo}`}
              className="text-left bg-card border border-border rounded-lg p-4 hover:border-agro-green/60 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md flex items-center justify-center bg-agro-yellow/10 text-agro-yellow text-xs font-mono font-bold">{b.codigo}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-lg">{b.nombre}</div>
                  <div className="text-[11px] text-text-muted truncate flex items-center gap-1"><MapPinIcon size={11} />{b.direccion || "Dirección pendiente"}</div>
                </div>
                <CaretRightIcon size={18} className="text-agro-green" />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {["factura", "nota_credito", "nota_debito"].map((t) => (
                  <span key={t} className={`text-[10px] px-2 py-0.5 rounded border ${b.autorizaciones?.[t] ? "border-agro-green/40 text-agro-green" : "border-border text-text-muted"}`}>
                    {TIPO_LABEL[t]} {b.autorizaciones?.[t] ? <CheckIcon size={11} weight="bold" className="inline align-[-2px]" aria-label="Configurada" /> : "—"}
                  </span>
                ))}
              </div>
              {b.completa ? (
                <div className="mt-2 text-[11px] text-agro-green flex items-center gap-1"><CheckCircleIcon size={12} weight="fill" /> Configuración completa</div>
              ) : (
                <div className="mt-2 text-[11px] text-agro-yellow flex items-start gap-1" data-testid={`org-faltantes-${b.codigo}`}>
                  <WarningIcon size={12} weight="fill" className="mt-0.5 flex-shrink-0" /> Por completar: {b.faltantes.join(", ")}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
      <PendingPanel pendientes={pendientes} empresas={[empresa]} isAdmin={isAdmin} onChanged={onChanged} />
    </div>
  );
}

/* ================================================================== sucursal */
function BranchView({ empresa, sucursalId, isAdmin, onChanged }) {
  const [b, setB] = useState(null);
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  const [rev, setRev] = useState(0);
  const [openReq, setOpenReq] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/settings/branches/${sucursalId}`);
      setB(data);
      setF({ direccion: data.direccion, telefono: data.telefono, ...data.config });
    } catch (e) { toast.error(apiErr(e)); }
  }, [sucursalId]);
  useEffect(() => { load(); }, [load, rev]);
  // Llegada con ancla (p. ej. desde el aviso de CAI en Ventas): desplaza a la sección
  const cargado = Boolean(b);
  useEffect(() => {
    if (!cargado || !window.location.hash) return;
    const t = setTimeout(() => document.getElementById(window.location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" }), 400);
    return () => clearTimeout(t);
  }, [cargado]);
  const set = (k, v) => setF((d) => ({ ...d, [k]: v }));
  const bump = () => { setRev((r) => r + 1); onChanged(); };
  const save = async () => {
    setSaving(true);
    try {
      const { direccion, telefono, celular, email, establecimiento, punto_emision } = f;
      await api.put(`/settings/branches/${sucursalId}`, { direccion, telefono, celular, email, establecimiento, punto_emision });
      toast.success(`Datos de la sucursal ${b.nombre} guardados. Las demás sucursales no cambian.`);
      bump();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  if (!b) return <div className="text-center text-text-muted py-12">Cargando…</div>;
  const branch = { id: b.id, codigo: b.codigo, nombre: b.nombre, direccion: b.direccion, telefono: b.telefono };
  const comun = { ...(empresa.identidad || {}), direccion: b.direccion, telefono: b.telefono };
  return (
    <div className="space-y-5">
      <div className="sticky top-0 z-20 bg-agro-green/10 border border-agro-green/40 rounded-lg px-4 py-2.5 flex flex-wrap items-center gap-3 backdrop-blur"
           data-testid="branch-editing-banner">
        <StorefrontIcon size={20} weight="duotone" className="text-agro-green" />
        <div className="text-sm">
          Editando: <span className="font-semibold">{empresa.codigo}</span> › <span className="font-semibold">{b.nombre}</span>
          <span className="text-text-muted text-xs ml-2">Los cambios aplican solo a esta sucursal.</span>
        </div>
        <nav className="flex flex-wrap gap-2 text-xs ml-auto">
          {[["sec-datos", "Datos"], ["sec-documentos", "Documentos"], ["sec-notas", "Notas"],
            ["sec-autorizaciones", "Autorizaciones"], ["sec-historial", "Historial"]].map(([id, t]) => (
            <a key={id} href={`#${id}`} className="px-2.5 py-1 rounded-full border border-border hover:border-agro-green hover:text-agro-green">{t}</a>
          ))}
        </nav>
      </div>

      {!b.completa && (
        <div className="bg-agro-yellow/10 border border-agro-yellow/40 rounded-md p-3 text-xs text-agro-yellow flex gap-2" data-testid="branch-faltantes">
          <WarningIcon size={16} weight="fill" className="flex-shrink-0" />
          <span>Para emitir documentos en {b.nombre} falta completar: {b.faltantes.join(", ")}. Mientras tanto el sistema
            indicará qué completar y no usará los datos de otra sucursal.</span>
        </div>
      )}

      <div id="sec-datos" className="scroll-mt-20">
        <Section title={`Datos de la sucursal ${b.nombre}`} icon={MapPinIcon}
                 desc="Dirección y contacto que imprimen los documentos de esta sucursal (la dirección fiscal de cada autorización CAI tiene prioridad en los documentos fiscales)."
                 action={isAdmin && (
                   <button onClick={save} disabled={saving} data-testid="save-branch"
                     className="bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
                     <FloppyDiskIcon size={16} weight="bold" /> {saving ? "Guardando…" : "Guardar sucursal"}
                   </button>)}>
          <Field label="Dirección">
            <textarea rows={2} value={f.direccion || ""} onChange={(e) => set("direccion", e.target.value)} disabled={!isAdmin}
                      className={`${inp} resize-none`} data-testid="branch-direccion" />
          </Field>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Teléfono"><input value={f.telefono || ""} onChange={(e) => set("telefono", e.target.value)} disabled={!isAdmin} className={inp} data-testid="branch-telefono" /></Field>
            <Field label="Celular"><input value={f.celular || ""} onChange={(e) => set("celular", e.target.value)} disabled={!isAdmin} className={inp} data-testid="branch-celular" /></Field>
            <Field label="Email"><input type="email" value={f.email || ""} onChange={(e) => set("email", e.target.value)} disabled={!isAdmin} className={inp} data-testid="branch-email" /></Field>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Establecimiento" hint="3 dígitos del rango SAR (ej. 001)">
              <input value={f.establecimiento || ""} onChange={(e) => set("establecimiento", e.target.value.replace(/\D/g, "").slice(0, 3))} disabled={!isAdmin}
                     className={`${inp} font-mono`} data-testid="branch-establecimiento" />
            </Field>
            <Field label="Punto de emisión" hint="3 dígitos (ej. 001)">
              <input value={f.punto_emision || ""} onChange={(e) => set("punto_emision", e.target.value.replace(/\D/g, "").slice(0, 3))} disabled={!isAdmin}
                     className={`${inp} font-mono`} data-testid="branch-punto" />
            </Field>
            <div className="text-[11px] text-text-muted self-end pb-2">
              Las autorizaciones CAI de esta sucursal deben usar este establecimiento y punto de emisión; el sistema
              impide correlativos duplicados entre sucursales.
            </div>
          </div>
        </Section>
      </div>

      <div id="sec-documentos" className="scroll-mt-20">
        <DocumentConfigSection comun={comun} branch={branch} canEdit={isAdmin} reloadKey={rev} onChanged={bump} openRequest={openReq} />
      </div>

      <div id="sec-notas" className="scroll-mt-20">
        <NotesSection branch={branch} reloadKey={rev} onEdit={(tipo) => setOpenReq({ tipo, n: Date.now() })} />
      </div>

      <div id="sec-autorizaciones" className="scroll-mt-20">
        <FiscalAuthorizations comun={comun} branch={branch} reloadKey={rev} onChanged={bump} />
      </div>

      <div id="sec-historial" className="scroll-mt-20">
        <BranchHistory branchId={b.id} reloadKey={rev} />
      </div>

      <div className="bg-agro-green/5 border border-agro-green/20 rounded-md p-4 flex gap-3 items-start">
        <InfoIcon size={20} className="text-agro-green flex-shrink-0 mt-0.5" weight="duotone" />
        <div className="text-xs text-text-secondary leading-relaxed">
          Cada documento queda vinculado a la sucursal que lo emitió y guarda sus datos, su dirección, su autorización
          fiscal y su nota: al consultarlo o reimprimirlo se ve exactamente como se emitió, aunque luego cambie esta
          configuración. La proforma usa numeración interna propia y no consume correlativos fiscales.
        </div>
      </div>
    </div>
  );
}

function NotesSection({ branch, reloadKey, onEdit }) {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    api.get("/settings/documents", { params: { branch_id: branch.id } })
      .then(({ data }) => setDocs((data.documentos || []).filter((d) => d.tiene_nota)))
      .catch(() => setDocs([]));
  }, [reloadKey, branch.id]);
  return (
    <Section title={`Notas y textos · ${branch.nombre}`} icon={NotePencilIcon}
             desc="Texto impreso en el recuadro NOTA de cada documento de esta sucursal. Cada documento guarda la nota vigente al emitirse.">
      <div className="grid md:grid-cols-3 gap-3">
        {docs.map((d) => (
          <div key={d.tipo} className="bg-app border border-border rounded-md p-3 flex flex-col" data-testid={`nota-${d.tipo}`}>
            <div className="text-xs font-semibold mb-1">{d.label}</div>
            <div className="text-[11px] text-text-secondary whitespace-pre-line line-clamp-6 flex-1">{d.config?.nota || "—"}</div>
            <button type="button" onClick={() => onEdit(d.tipo)} data-testid={`nota-edit-${d.tipo}`}
              className="mt-2 self-start text-xs text-agro-green hover:underline">Editar nota</button>
          </div>
        ))}
      </div>
    </Section>
  );
}

const ENTIDAD = { branch_settings: "Datos de la sucursal", branch_document_config: "Configuración de documento" };

function BranchHistory({ branchId, reloadKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/settings/branches/${branchId}/history`).then(({ data }) => setRows(data || [])).catch(() => setRows([]));
  }, [branchId, reloadKey]);
  return (
    <Section title="Historial de configuración" icon={ClockCountdownIcon}
             desc="Cambios de datos, documentos y notas de esta sucursal. El historial de autorizaciones fiscales está en la sección anterior.">
      {rows.length === 0 ? <div className="text-xs text-text-muted">Sin cambios registrados.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="branch-history">
            <thead className="text-[10px] uppercase tracking-wider text-text-muted">
              <tr><th className="p-2 text-left">Fecha</th><th className="p-2 text-left">Usuario</th><th className="p-2 text-left">Qué cambió</th><th className="p-2 text-left">Campos</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/40">
                  <td className="p-2 whitespace-nowrap">{fmtDate(r.timestamp)}</td>
                  <td className="p-2">{r.user_email}</td>
                  <td className="p-2">{ENTIDAD[r.entity] || r.entity}{r.entity === "branch_document_config" ? ` · ${String(r.entity_id).split(":")[1]}` : ""}</td>
                  <td className="p-2 text-text-muted">{Object.keys(r.after || {}).join(", ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/* ================================================================== pendientes de la consolidación */
function PendingPanel({ pendientes, empresas, isAdmin, onChanged }) {
  const sucs = pendientes?.sucursales || [];
  const bods = pendientes?.bodegas || [];
  const destinos = empresas.flatMap((e) => e.sucursales);
  if (!sucs.length && !bods.length) return null;
  return (
    <div className="bg-card border border-agro-yellow/40 rounded-lg overflow-hidden" data-testid="org-pendientes">
      <div className="px-5 py-3 border-b border-agro-yellow/30 bg-agro-yellow/5 flex gap-3 items-start">
        <ShieldCheckIcon size={20} className="text-agro-yellow" weight="duotone" />
        <div>
          <div className="font-heading text-agro-yellow">Registros por resolver</div>
          <div className="text-[11px] text-text-muted mt-0.5">
            Ubicaciones que no corresponden a la empresa pero tienen operaciones vinculadas. Ya no están disponibles en
            ningún módulo; sus registros se conservan tal como están hasta que decidas qué hacer (no se reasignan solos).
          </div>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {sucs.map((s) => <PendingBranch key={s.id} s={s} destinos={destinos} isAdmin={isAdmin} onChanged={onChanged} />)}
        {bods.map((w) => (
          <div key={w.id} className="text-xs bg-app border border-border rounded-md p-3">
            Bodega <span className="font-mono">{w.codigo}</span> · {w.nombre}: tiene existencias o movimientos. Traslada sus
            existencias y desactívala en <Link to="/inventario/bodegas" className="text-agro-green hover:underline">Bodegas</Link>.
          </div>
        ))}
      </div>
    </div>
  );
}

function PendingBranch({ s, destinos, isAdmin, onChanged }) {
  const [destino, setDestino] = useState("");
  const [motivo, setMotivo] = useState("");
  const resolver = async (accion) => {
    if (!motivo.trim()) return toast.error("Indica el motivo de la decisión");
    if (accion === "reasignar" && !destino) return toast.error("Elige la sucursal a la que pertenecen estos registros");
    const txt = accion === "reasignar"
      ? "Los registros vinculados se asignarán a la sucursal elegida. ¿Confirmas?"
      : "Los registros se conservan con la sucursal retirada (histórico). ¿Confirmas?";
    if (!window.confirm(txt)) return;
    try {
      await api.post(`/org/branches/${s.id}/resolve`, { accion, destino_branch_id: destino, motivo });
      toast.success("Resolución registrada");
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const refs = Object.entries(s.referencias || {}).map(([k, v]) => `${k}: ${v}`).join(" · ");
  return (
    <div className="text-xs bg-app border border-border rounded-md p-3" data-testid={`pendiente-${s.codigo}`}>
      <div className="font-semibold">Sucursal retirada: {s.nombre} <span className="font-mono text-text-muted">({s.codigo})</span></div>
      <div className="text-text-muted mt-0.5">Registros vinculados — {refs || "—"}</div>
      {isAdmin && (
        <div className="grid md:grid-cols-[1fr_1fr_auto_auto] gap-2 mt-2">
          <select value={destino} onChange={(e) => setDestino(e.target.value)} className="bg-card border border-border rounded px-2 py-1.5">
            <option value="">Sucursal real (si se conoce)…</option>
            {destinos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
          <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo / respaldo de la decisión"
                 className="bg-card border border-border rounded px-2 py-1.5" />
          <button onClick={() => resolver("reasignar")} className="px-3 py-1.5 rounded border border-agro-green/50 text-agro-green hover:bg-agro-green/10">Reasignar</button>
          <button onClick={() => resolver("conservar_historico")} className="px-3 py-1.5 rounded border border-border text-text-secondary hover:text-text-primary">Conservar como histórico</button>
        </div>
      )}
    </div>
  );
}

function Section({ title, desc, icon: Icon, action, children }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-dirty-scope>
      <div className="px-5 py-3 border-b border-border/50 flex gap-3 items-start">
        {Icon && <Icon size={20} className="text-text-secondary" weight="duotone" />}
        <div className="flex-1 min-w-0">
          <div className="font-heading">{title}</div>
          {desc && <div className="text-[11px] text-text-muted mt-0.5">{desc}</div>}
        </div>
        {action}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-text-muted mb-1.5 font-medium">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-text-muted/80 mt-1">{hint}</div>}
    </div>
  );
}
